'use client'

import { motion } from 'motion/react'

import { cn } from '@/utilities/ui'

import type { StepDirection } from './StepFooter'

const STAGING_SCALE = 0.75
const CAROUSEL_DURATION = 0.35

/** Initial scale/opacity when this slot is “entering” from center (old current step moved to side). */
const fromCenter = { scale: 1, opacity: 1 }
/** Initial scale/opacity when this slot is “entering” from off-screen (new next/previous). */
const fromEdge = { scale: 0.5, opacity: 0.6 }
/** Rest state for a staging panel. */
const rest = { scale: STAGING_SCALE, opacity: 0.9 }

function getVariants(position: 'left' | 'right', direction: StepDirection | null) {
  if (!direction) return { initial: rest, animate: rest }
  if (direction === 'next') {
    return position === 'left'
      ? { initial: fromCenter, animate: rest } // old center moved left
      : { initial: fromEdge, animate: rest } // new next from right
  }
  return position === 'right'
    ? { initial: fromCenter, animate: rest } // old center moved right
    : { initial: fromEdge, animate: rest } // new previous from left
}

export const StagingStep = ({
  position,
  direction,
  children,
}: {
  position: 'left' | 'right'
  direction?: StepDirection | null
  children: React.ReactNode
}) => {
  const variants = getVariants(position, direction ?? null)
  return (
    <motion.div
      initial={variants.initial}
      animate={variants.animate}
      transition={{
        duration: CAROUSEL_DURATION,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={cn(
        'fixed top-[50%] z-50 hidden min-h-75 w-full max-w-lg translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg sm:grid',
        'origin-center',
        // Keep partially behind the centered dialog: anchor to 50% with 8rem overlap; clamp on small viewports
        position === 'left'
          ? 'left-[max(0.5rem,calc(50%-40rem))]' // right edge at 50% - 8rem (8rem under dialog)
          : 'left-[min(calc(50%+8rem),calc(100vw-33rem))]', // left at 50%+8rem, or flush right on narrow screens
      )}
    >
      {children}
    </motion.div>
  )
}
